import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui'
import * as React from 'react'

import { cn } from '@/lib/utils'

/*
 * Menu de ações do GEF: o `dropdown-menu` do shadcn/ui, sobre o DropdownMenu
 * do Radix (teclado, foco devolvido, Esc fecha). O que difere do shadcn:
 *
 * - Só o que a Área do Gestor usa: raiz, gatilho, conteúdo, grupo, item,
 *   rótulo e separador. Ficam de fora os itens com caixa de seleção, com
 *   escolha única, os submenus e os atalhos.
 * - O painel usa os tokens `--gef-opcoes-*` da lista de seleção (fundo, borda,
 *   canto, sombra e camada), então o menu "…" e a lista de escolha têm a mesma
 *   cara; o item em foco usa `--gef-opcao-foco-fundo`. Itens de 36px.
 * - `destructive` pinta o item de vermelho de erro ("Revogar acesso").
 * - Sem animação de entrada, sem classes `dark:` e sem medida literal.
 */

function DropdownMenu({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
}

function DropdownMenuTrigger({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return <DropdownMenuPrimitive.Trigger data-slot="dropdown-menu-trigger" {...props} />
}

function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(
          'z-(--gef-opcoes-camada) min-w-(--radix-dropdown-menu-trigger-width) overflow-x-hidden overflow-y-auto',
          'max-h-(--radix-dropdown-menu-content-available-height)',
          'rounded-(--gef-opcoes-raio) border border-(--gef-opcoes-borda)',
          'bg-(--gef-opcoes-fundo) text-(--gef-opcoes-texto) shadow-(--gef-opcoes-sombra)',
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}

function DropdownMenuGroup({ ...props }: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  return <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
}

function DropdownMenuItem({
  className,
  destructive = false,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & { destructive?: boolean }) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      className={cn(
        'relative flex h-9 cursor-pointer items-center gap-2 px-(--gef-opcao-recuo)',
        'text-base font-medium outline-hidden select-none',
        'data-[highlighted]:bg-(--gef-opcao-foco-fundo)',
        'data-[disabled]:pointer-events-none data-[disabled]:opacity-(--gef-desabilitado)',
        '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
        destructive && 'text-destructive',
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuLabel({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label>) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      className={cn('px-(--gef-opcao-recuo) py-1.5 text-xs text-muted-foreground', className)}
      {...props}
    />
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn('my-1 h-px bg-border', className)}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
}
